import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, ButtonGroup, Alert, Spinner, Placeholder, Badge, Table, Modal, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
    getTradeDetails,
    updateTrade,
    getTrades,
    getAdjacentUnanalyzedTrade,
    deleteTrade,
    addWatchlistTicker
} from '../services/api';
import ArmorEdit from '../components/ArmorEdit';


// Custom styles for the component
const customStyles = `
    /* CSS Variables for consistent theming */
    :root {
        --card-bg: #ffffff;
        --border-color: #dee2e6;
        --shadow-color: rgba(0, 0, 0, 0.1);
        --text-primary: #212529;
        --bg-secondary: #f8f9fa;
        --hover-bg: #f1f5f9;
    }
    
    .ticker-symbol-button {
        border: none;
        background: transparent;
        padding: 0;
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        font-weight: 700;
        font-size: 1.5rem;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        color: var(--text-primary);
        cursor: pointer;
    }
    
    .ticker-symbol-button .ticker-symbol-text {
        color: inherit;
    }
    
    .ticker-symbol-button:hover .ticker-symbol-text,
    .ticker-symbol-button:focus .ticker-symbol-text {
        text-decoration: underline;
    }
    
    .ticker-symbol-hint {
        font-size: 0.8rem;
        color: #6b7280;
    }
    
    [data-theme="dark"] {
        --card-bg: #1f2937;
        --border-color: #374151;
        --shadow-color: rgba(0, 0, 0, 0.3);
        --text-primary: #f9fafb;
        --bg-secondary: #374151;
        --hover-bg: #4b5563;
    }
    
    /* Enhanced card styling for maximum contrast and prominence */
    .trade-details-card {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        transition: all 0.3s ease-in-out;
        position: relative;
    }
    
    .trade-details-card:hover {
        box-shadow: 0 8px 24px var(--shadow-color) !important;
        transform: translateY(-2px);
    }
    
    /* Light theme card enhancements */
    [data-theme="light"] .trade-details-card {
        background-color: #ffffff !important;
        border: 2px solid #d1d5db !important;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 4px 20px rgba(0, 0, 0, 0.15) !important;
    }
    
    [data-theme="light"] .trade-details-card:hover {
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(0, 0, 0, 0.25) !important;
        border-color: #9ca3af !important;
    }
    
    /* Dark theme card enhancements */
    [data-theme="dark"] .trade-details-card {
        background-color: #1f2937 !important;
        border: 2px solid #4b5563 !important;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.05), 0 4px 20px rgba(0, 0, 0, 0.5) !important;
    }
    
    [data-theme="dark"] .trade-details-card:hover {
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.6) !important;
        border-color: #6b7280 !important;
        background-color: #374151 !important;
    }
    
    /* Enhanced table styling for maximum contrast and prominence */
    .trade-details-table-container {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        overflow: hidden !important;
        transition: all 0.3s ease-in-out;
    }
    
    .trade-details-table-container:hover {
        box-shadow: 0 8px 24px var(--shadow-color) !important;
    }
    
    /* Light theme table enhancements */
    [data-theme="light"] .trade-details-table-container {
        background-color: #ffffff !important;
        border: 2px solid #d1d5db !important;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 4px 20px rgba(0, 0, 0, 0.15) !important;
    }
    
    [data-theme="light"] .trade-details-table-container:hover {
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(0, 0, 0, 0.25) !important;
        border-color: #9ca3af !important;
    }
    
    /* Dark theme table enhancements */
    [data-theme="dark"] .trade-details-table-container {
        background-color: #1f2937 !important;
        border: 2px solid #4b5563 !important;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.05), 0 4px 20px rgba(0, 0, 0, 0.5) !important;
    }
    
    [data-theme="dark"] .trade-details-table-container:hover {
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.6) !important;
        border-color: #6b7280 !important;
        background-color: #374151 !important;
    }
    
    /* Remove gap at bottom of table */
    .trade-details-table-container .table {
        margin-bottom: 0 !important;
    }
    
    .trade-details-table-container .trade-details-table {
        margin-bottom: 0 !important;
        background-color: var(--card-bg) !important;
    }
    
    /* Enhanced table header styling with hover effects */
    .trade-details-table-container .trade-details-table thead th {
        background-color: #f8f9fa !important;
        border-bottom: 2px solid var(--border-color) !important;
        font-weight: 600 !important;
        color: var(--text-primary) !important;
        padding: 8px 12px !important;
        transition: all 0.2s ease-in-out !important;
    }
    
    .trade-details-table-container .trade-details-table thead th:hover {
        background-color: #e9ecef !important;
        transform: translateY(-1px) !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
    }
    
    [data-theme="light"] .trade-details-table-container .trade-details-table thead th {
        background-color: #f8f9fa !important;
        border-bottom: 2px solid #d1d5db !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .trade-details-table-container .trade-details-table thead th:hover {
        background-color: #e9ecef !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
    }
    
    [data-theme="dark"] .trade-details-table-container .trade-details-table thead th {
        background-color: #374151 !important;
        border-bottom: 2px solid #4b5563 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .trade-details-table-container .trade-details-table thead th:hover {
        background-color: #4b5563 !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3) !important;
    }
    
    /* Enhanced table row styling */
    .trade-details-table-container .trade-details-table tbody tr {
        border-bottom: 1px solid var(--border-color) !important;
        font-size: 0.95rem !important;
    }

    /* Enhanced table cell styling */
    .trade-details-table-container .trade-details-table tbody td {
        padding: 8px 12px !important;
        border-bottom: 1px solid var(--border-color) !important;
        vertical-align: middle !important;
    }

    [data-theme="light"] .trade-details-table-container .trade-details-table tbody td {
        border-bottom: 1px solid #e5e7eb !important;
    }

    [data-theme="dark"] .trade-details-table-container .trade-details-table tbody td {
        border-bottom: 1px solid #374151 !important;
    }

    .sold-row {
        background-color: #f3f4f6 !important;
    }
    [data-theme="dark"] .sold-row {
        background-color: #1f2937 !important;
    }
    .sold-row td {
        background-color: #eef2f7 !important;
    }
    [data-theme="dark"] .sold-row td {
        background-color: #1f2937 !important;
    }
    .trade-details-table .sold-row td {
        background-color: #e7ecf2 !important;
        border-top: 1px solid #cbd5e1 !important;
    }
    [data-theme="dark"] .trade-details-table .sold-row td {
        background-color: #1f2937 !important;
        border-top: 1px solid #374151 !important;
    }
    /* Prevent horizontal scrollbar */
    .trade-details-table-container {
        overflow-x: hidden !important;
    }
    
    .trade-details-table {
        width: 100% !important;
        table-layout: fixed !important;
    }
    
    /* Ensure table cells don't overflow */
    .trade-details-table td,
    .trade-details-table th {
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
    }

    /* Prevent table dimension changes during hover */
    .trade-details-table-container {
        overflow-x: hidden !important;
        overflow-y: hidden !important;
    }
    
    .trade-details-table {
        width: 100% !important;
        table-layout: fixed !important;
        margin: 0 !important;
    }
    
    /* Ensure table cells don't overflow and maintain consistent size */
    .trade-details-table td,
    .trade-details-table th {
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        box-sizing: border-box !important;
    }
    
    
    /* Purchase position card styling */
    .purchase-position-card {
        background: linear-gradient(135deg, var(--card-bg) 0%, var(--bg-secondary) 100%) !important;
        border: 2px solid var(--border-color) !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        transition: all 0.3s ease-in-out;
        position: relative;
        overflow: hidden;
    }
    
    .purchase-position-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(90deg, #0d6efd, #6610f2, #dc3545);
        opacity: 0.8;
    }
    
    .purchase-position-card:hover {
        transform: translateY(-4px) !important;
        box-shadow: 0 8px 32px var(--shadow-color) !important;
        border-color: #0d6efd !important;
    }
    
    [data-theme="light"] .purchase-position-card {
        background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%) !important;
        border: 2px solid #d1d5db !important;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 4px 20px rgba(0, 0, 0, 0.15) !important;
    }
    
    [data-theme="light"] .purchase-position-card:hover {
        box-shadow: 0 0 0 1px rgba(13, 110, 253, 0.2), 0 8px 32px rgba(0, 0, 0, 0.25) !important;
        border-color: #0d6efd !important;
    }
    
    [data-theme="dark"] .purchase-position-card {
        background: linear-gradient(135deg, #1f2937 0%, #374151 100%) !important;
        border: 2px solid #4b5563 !important;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.05), 0 4px 20px rgba(0, 0, 0, 0.5) !important;
    }
    
    [data-theme="dark"] .purchase-position-card:hover {
        box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.3), 0 8px 32px rgba(0, 0, 0, 0.6) !important;
        border-color: #3b82f6 !important;
    }
    
    /* Shield badge styling */
    .shield-badge {
        font-size: 0.75rem !important;
        padding: 0.25rem 0.5rem !important;
        border-radius: 0.375rem !important;
        font-weight: 500 !important;
    }
    
    /* Purchase price highlight */
    .purchase-price-highlight {
        background: linear-gradient(135deg, #0d6efd, #6610f2) !important;
        -webkit-background-clip: text !important;
        -webkit-text-fill-color: transparent !important;
        background-clip: text !important;
        font-weight: 700 !important;
    }
    
    [data-theme="dark"] .purchase-price-highlight {
        background: linear-gradient(135deg, #3b82f6, #8b5cf6) !important;
        -webkit-background-clip: text !important;
        -webkit-text-fill-color: transparent !important;
        background-clip: text !important;
    }
    
    /* Enhanced total row styling - match header styling exactly */
    .trade-details-table-container .trade-details-table .table-total {
        background-color: var(--bg-secondary) !important;
        border-top: 2px solid var(--border-color) !important;
        border-bottom: 2px solid var(--border-color) !important;
        font-weight: 600 !important;
        color: var(--text-primary) !important;
    }
    
    .trade-details-table-container .trade-details-table .table-total td {
        background-color: var(--bg-secondary) !important;
        color: var(--text-primary) !important;
        padding: 8px 12px !important;
        border-bottom: none !important;
    }
    
    .trade-details-table-container .trade-details-table .table-total:hover {
        background-color: var(--hover-bg) !important;
        cursor: default !important;
        transform: none !important;
        box-shadow: none !important;
    }
    
    [data-theme="light"] .trade-details-table-container .trade-details-table .table-total {
        background-color: #f8f9fa !important;
        border-top: 2px solid #d1d5db !important;
        border-bottom: 2px solid #d1d5db !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .trade-details-table-container .trade-details-table .table-total td {
        background-color: #f8f9fa !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .trade-details-table-container .trade-details-table .table-total:hover {
        background-color: #e9ecef !important;
    }
    
    [data-theme="dark"] .trade-details-table-container .trade-details-table .table-total {
        background-color: #374151 !important;
        border-top: 2px solid #4b5563 !important;
        border-bottom: 2px solid #4b5563 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .trade-details-table-container .trade-details-table .table-total td {
        background-color: #374151 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .trade-details-table-container .trade-details-table .table-total:hover {
        background-color: #4b5563 !important;
    }
    
    /* Enhanced profit/loss styling for summary row */
    .table-total .text-success {
        color: #28a745 !important;
        font-weight: 700 !important;
        text-shadow: 0 1px 2px rgba(40, 167, 69, 0.2);
    }
    
    .table-total .text-danger {
        color: #dc3545 !important;
        font-weight: 700 !important;
        text-shadow: 0 1px 2px rgba(220, 53, 69, 0.2);
    }
    
    [data-theme="dark"] .table-total .text-success {
        color: #75b798 !important;
        font-weight: 700 !important;
        text-shadow: 0 1px 2px rgba(117, 183, 152, 0.3);
    }
    
    [data-theme="dark"] .table-total .text-danger {
        color: #f5c6cb !important;
        font-weight: 700 !important;
        text-shadow: 0 1px 2px rgba(245, 198, 203, 0.3);
    }
`;

const SHARES_TOLERANCE = 0.001;

// Helper function to format date with weekday
const formatDateWithWeekday = (dateString) => {
    const date = new Date(dateString);
    const dateOptions = { 
        month: 'numeric', 
        day: 'numeric', 
        year: 'numeric'
    };
    const weekdayOptions = { 
        weekday: 'short'
    };
    const dateStr = date.toLocaleDateString(undefined, dateOptions);
    const weekdayStr = date.toLocaleDateString(undefined, weekdayOptions);
    return `${dateStr} ${weekdayStr}`;
};

const formatDateForApi = (date) => date.toISOString().split('T')[0];

const LESSON_RATING_FIELDS = [
    { key: 'entry', label: 'Entry' },
    { key: 'timing', label: 'Timing' },
    { key: 'stop', label: 'Stop' },
    { key: 'size', label: 'Size' },
    { key: 'execution', label: 'Execution' }
];

const getPostAnalysisDateRange = (period) => {
    const endDate = new Date();
    let startDate = null;

    if (period === '1month') {
        startDate = new Date(endDate);
        startDate.setMonth(startDate.getMonth() - 1);
    } else if (period === '2months') {
        startDate = new Date(endDate);
        startDate.setMonth(startDate.getMonth() - 2);
    } else if (period === '3months') {
        startDate = new Date(endDate);
        startDate.setMonth(startDate.getMonth() - 3);
    } else if (period === '6months') {
        startDate = new Date(endDate);
        startDate.setMonth(startDate.getMonth() - 6);
    } else if (period === '1year') {
        startDate = new Date(endDate);
        startDate.setFullYear(startDate.getFullYear() - 1);
    }

    return { startDate, endDate };
};

// Helper function to check if shares are effectively zero:
const isEffectivelyZero = (shares) => Math.abs(shares) < SHARES_TOLERANCE;

const TradeDetails = () => {
    const { tradeId } = useParams();
    const navigate = useNavigate();
    const latestTradeIdRef = useRef(tradeId);
    const [trade, setTrade] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tickerInput, setTickerInput] = useState('');
    const [tickerLookupState, setTickerLookupState] = useState({
        loading: false,
        error: ''
    });
    const [showTickerModal, setShowTickerModal] = useState(false);
    const [tickerSearchStatus, setTickerSearchStatus] = useState('active');
    const [matchingTrades, setMatchingTrades] = useState([]);

    const [editingPostAnalysis, setEditingPostAnalysis] = useState(false);
    const [savingPostAnalysis, setSavingPostAnalysis] = useState(false);
    const [postAnalysisDraft, setPostAnalysisDraft] = useState({
        post_analysis_rating: null,
        post_analysis_analysis: '',
        post_analysis_lessons: ''
    });
    const [lessonRatings, setLessonRatings] = useState({
        entry: null,
        timing: null,
        stop: null,
        size: null,
        execution: null
    });
    const [lessonsFreeText, setLessonsFreeText] = useState('');
    const [nextClosedTradeState, setNextClosedTradeState] = useState({
        loading: false,
        error: ''
    });
    const [prevClosedTradeState, setPrevClosedTradeState] = useState({
        loading: false,
        error: '',
        hasPrev: null
    });
    const [deleteTradeState, setDeleteTradeState] = useState({
        loading: false,
        error: ''
    });
    const [watchlistAddState, setWatchlistAddState] = useState({
        loading: false,
        error: '',
        success: false
    });
    const [remainingPostAnalysisPeriod, setRemainingPostAnalysisPeriod] = useState(() => {
        if (typeof window === 'undefined') {
            return '1month';
        }
        return localStorage.getItem('tradeDetailsPostAnalysisPeriod') || '1month';
    });
    const [remainingPostAnalysisState, setRemainingPostAnalysisState] = useState({
        loading: false,
        error: '',
        count: null
    });
    const [txSort, setTxSort] = useState(() => {
        try {
            const stored = localStorage.getItem('tradeDetailsTxSort');
            if (stored) return JSON.parse(stored);
        } catch {}
        return { col: 'date', dir: 'asc' };
    });
    const [txTypeFilter, setTxTypeFilter] = useState('BOTH');
    const [showBuySlots, setShowBuySlots] = useState(() => {
        try {
            const stored = localStorage.getItem('tradeDetailsShowBuySlots');
            if (stored !== null) return JSON.parse(stored);
        } catch {}
        return true;
    });

    useEffect(() => {
        try {
            localStorage.setItem('tradeDetailsShowBuySlots', JSON.stringify(showBuySlots));
        } catch {}
    }, [showBuySlots]);

    useEffect(() => {
        fetchTradeDetails();
        setNextClosedTradeState({ loading: false, error: '' });
        setPrevClosedTradeState({ loading: false, error: '', hasPrev: null });
        setDeleteTradeState({ loading: false, error: '' });
        setWatchlistAddState({ loading: false, error: '', success: false });
        // fetchTradeDetails intentionally omitted: it's part of a deep chain of trade-state
        // helpers declared later in this file, and this effect must only fire on tradeId change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tradeId]);

    useEffect(() => {
        latestTradeIdRef.current = tradeId;
    }, [tradeId]);

    useEffect(() => {
        if (trade?.status === 'CLOSED') {
            fetchRemainingPostAnalysisCount();
            refreshPrevClosedTradeAvailability();
        } else {
            setRemainingPostAnalysisState({ loading: false, error: '', count: null });
            setPrevClosedTradeState({ loading: false, error: '', hasPrev: null });
        }
        // fetchRemainingPostAnalysisCount/refreshPrevClosedTradeAvailability intentionally
        // omitted: same rationale as the effect above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trade?.status, trade?.trade_id, remainingPostAnalysisPeriod]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        localStorage.setItem('tradeDetailsPostAnalysisPeriod', remainingPostAnalysisPeriod);
    }, [remainingPostAnalysisPeriod]);



    // DO NOT subscribe to ticker updates on Trade Details page
    // This prevents any automatic refreshing that could interrupt user's work
    // (e.g., editing post-analysis notes)

    const fetchTradeDetails = async (isBackgroundUpdate = false) => {
        try {
            // Only show loading spinner on initial load, not on background price updates
            if (!isBackgroundUpdate) {
                setLoading(true);
            }
            const response = await getTradeDetails(tradeId);
            setTrade(response.data);
            setTickerInput(response.data.ticker_symbol || '');
            setTickerLookupState({ loading: false, error: '' });
            const lessonState = parseLessonRatings(response.data.post_analysis_lessons || '');
            setPostAnalysisDraft({
                post_analysis_rating: getRatingFromLessons(lessonState.ratings),
                post_analysis_analysis: response.data.post_analysis_analysis || '',
                post_analysis_lessons: buildLessonsText(lessonState.ratings, lessonState.freeText)
            });
            setLessonRatings(lessonState.ratings);
            setLessonsFreeText(lessonState.freeText);
            const hasAnyRating = Object.values(lessonState.ratings).some((value) => value !== null);
            const hasAnalysis = Boolean(response.data.post_analysis_analysis && response.data.post_analysis_analysis.trim());
            const hasLessons = Boolean(lessonState.freeText && lessonState.freeText.trim());
            const hasPostRating = response.data.post_analysis_rating !== null && response.data.post_analysis_rating !== undefined;
            setEditingPostAnalysis(!(hasAnyRating || hasAnalysis || hasLessons || hasPostRating));
            setError('');
        } catch (err) {
            console.error('Error fetching trade details:', err);
            setError('Failed to load trade details');
        } finally {
            if (!isBackgroundUpdate) {
                setLoading(false);
            }
        }
    };

    const handleOpenTickerModal = () => {
        setTickerInput('');
        setTickerLookupState({ loading: false, error: '' });
        setMatchingTrades([]);
        setTickerSearchStatus('active');
        setShowTickerModal(true);
    };

    const handleCloseTickerModal = () => {
        if (tickerLookupState.loading) return;
        setShowTickerModal(false);
        setMatchingTrades([]);
    };

    const handleTickerLookup = async (symbolInput) => {
        if (tickerLookupState.loading) {
            return;
        }

        const normalizedSymbol = (symbolInput || tickerInput).trim().toUpperCase();

        if (!normalizedSymbol) {
            setTickerLookupState(prev => ({ ...prev, error: 'Enter a ticker symbol' }));
            return;
        }

        setTickerLookupState({ loading: true, error: '' });
        setMatchingTrades([]);

        try {
            const response = await getTrades({
                status: tickerSearchStatus || 'active',
                ticker: normalizedSymbol,
                page: 1,
                per_page: 50
            });

            const trades = response.data?.trades || [];
            const totalMatches = response.data?.pagination?.total ?? trades.length;

            if (trades.length === 0) {
                setTickerLookupState({ loading: false, error: `No active trade found for ${normalizedSymbol}` });
                return;
            }

            if (totalMatches !== 1) {
                setTickerLookupState({ loading: false, error: '' });
                setMatchingTrades(trades);
                return;
            }

            setTickerLookupState({ loading: false, error: '' });
            handleTradeSelection(trades[0], normalizedSymbol);
        } catch (lookupErr) {
            console.error('Error loading trade by ticker:', lookupErr);
            setTickerLookupState({ loading: false, error: 'Unable to load trade for this ticker' });
        }
    };

    const handleTickerInputChange = (value) => {
        setTickerInput(value.toUpperCase());
        if (matchingTrades.length > 0) {
            setMatchingTrades([]);
        }
        if (tickerLookupState.error) {
            setTickerLookupState(prev => ({ ...prev, error: '' }));
        }
    };

    const handleTickerInputKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            const symbol = event.currentTarget.value.trim().toUpperCase();
            if (symbol) {
                handleTickerLookup(symbol);
            }
        }
    };

    const handleTradeSelection = (selectedTrade, symbolOverride = null) => {
        if (!selectedTrade) return;
        setShowTickerModal(false);
        setMatchingTrades([]);
        setTickerLookupState({ loading: false, error: '' });
        if (symbolOverride) {
            setTickerInput(symbolOverride);
        } else if (selectedTrade.ticker_symbol) {
            setTickerInput(selectedTrade.ticker_symbol);
        }
        navigate(`/trades/${selectedTrade.trade_id}`);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    const handleTxSort = (col) => {
        setTxSort(prev => {
            const next = { col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' };
            localStorage.setItem('tradeDetailsTxSort', JSON.stringify(next));
            return next;
        });
    };
    const txSortIndicator = (col) => txSort.col === col ? (txSort.dir === 'asc' ? ' ▲' : ' ▼') : null;

    const formatCurrencyNoCents = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const formatPercentage = (percent) => {
        return `${(percent || 0).toFixed(2)}%`;
    };

    const getGainLossColor = (value) => {
        if (value > 0) return 'success';
        if (value < 0) return 'danger';
        return 'secondary';
    };

    const getGainLossTextColor = (value) => {
        if (value > 0) return 'text-success';
        if (value < 0) return 'text-danger';
        return 'text-secondary';
    };



    const renderPurchasePositions = () => {
        // Group BUY transactions by price
        const buyTransactions = trade.transactions?.filter(tx => tx.transaction_type === 'BUY') || [];
        const priceGroups = {};
        
        buyTransactions.forEach(tx => {
            const price = tx.price_per_share;
            if (!priceGroups[price]) {
                priceGroups[price] = {
                    price: price,
                    transactions: [],
                    totalShares: 0,
                    totalAmount: 0,
                    latestDate: null
                };
            }
            priceGroups[price].transactions.push(tx);
            priceGroups[price].totalShares += tx.shares;
            priceGroups[price].totalAmount += tx.shares * tx.price_per_share;
            
            // Track the latest date for this price group
            const txDate = new Date(tx.transaction_date);
            if (!priceGroups[price].latestDate || txDate > priceGroups[price].latestDate) {
                priceGroups[price].latestDate = txDate;
            }
        });
        
        // Mark price groups whose shares have been fully sold (LIFO), so they can be
        // pushed to the bottom and rendered as a compact "sold" card. Also track the
        // shares still held for this lot (post-LIFO), so shield allocation is based on
        // what's actually left, not the original buy quantity.
        Object.values(priceGroups).forEach(priceGroup => {
            const lotTxIds = new Set(priceGroup.transactions.map(tx => tx.transaction_id));
            const remainingLotShares = (trade.lots || []).reduce((total, lot) => (
                lotTxIds.has(lot.transaction_id) ? total + (Number(lot.shares) || 0) : total
            ), 0);
            priceGroup.remainingShares = remainingLotShares;
            priceGroup.isSoldOut = priceGroup.totalShares > 0 && isEffectivelyZero(remainingLotShares);
        });

        // Sort active positions first (most recent first), sold-out positions last.
        const uniquePrices = Object.values(priceGroups).sort((a, b) => {
            if (a.isSoldOut !== b.isSoldOut) return a.isSoldOut ? 1 : -1;
            if (!a.latestDate && !b.latestDate) return 0;
            if (!a.latestDate) return 1;
            if (!b.latestDate) return -1;
            return b.latestDate - a.latestDate;
        });

        // An overall (buy_transaction_id-less) shield blends every lot's cost basis together --
        // adding a per-lot stop on top of it would let the two mechanisms fight over the same
        // shares, so each lot card blocks new per-lot stops until the overall one is deleted.
        const hasOverallShield = (trade.shields || []).some(shield => !shield.buy_transaction_id);

        // Build the left content (original stop loss & purchase positions section)
        const leftContent = (
            <div>
                <div className="d-flex align-items-center mb-3">
                    <h5 className="mb-0 me-2">
                        <i className="bi bi-shield-check me-2"></i>
                        Stop Losses & Purchase Positions
                    </h5>
                    <Badge bg="info" className="ms-2">
                        {uniquePrices.length} {uniquePrices.length === 1 ? 'Position' : 'Positions'}
                    </Badge>
                    {trade && trade.status !== 'CLOSED' && trade.account && trade.shields && trade.shields.length > 0 && (() => {
                        // Calculate combined overall effect of all stop losses across all positions
                        const totalShieldPL = trade.shields.reduce((total, shield) => {
                            return total + (shield.amount_from_cost || 0);
                        }, 0);
                        
                        const totalShares = trade.total_shares || 0;
                        const averageCostBasis = trade.total_cost_basis || 0;
                        const costBasisValue = totalShares * averageCostBasis;
                        const overallEffectPercentage = costBasisValue > 0 ? (totalShieldPL / costBasisValue) * 100 : 0;
                        
                        return (
                            <Badge 
                                bg={overallEffectPercentage >= 0 ? 'success' : 'danger'} 
                                className="ms-2"
                                style={{ fontSize: '0.9rem' }}
                            >
                                Combined Shield Effect: {overallEffectPercentage >= 0 ? '+' : ''}{overallEffectPercentage.toFixed(2)}% ({totalShieldPL >= 0 ? '+' : ''}{formatCurrency(totalShieldPL)})
                            </Badge>
                        );
                    })()}
                    <div className="ms-auto d-flex align-items-center gap-3 flex-wrap">
                        <Form.Check
                            type="switch"
                            id="show-buy-slots-toggle"
                            label="Positions"
                            checked={showBuySlots}
                            onChange={(e) => setShowBuySlots(e.target.checked)}
                        />
                        {trade?.status === 'CLOSED' && (
                            <>
                                <div className="d-flex align-items-center gap-2">
                                    <span className="text-muted small">Unanalyzed left</span>
                                    <span className="fw-semibold small">
                                        {remainingPostAnalysisState.loading ? '...' : (remainingPostAnalysisState.count ?? '-')}
                                    </span>
                                </div>
                                <Form.Select
                                    size="sm"
                                    value={remainingPostAnalysisPeriod}
                                    onChange={(event) => setRemainingPostAnalysisPeriod(event.target.value)}
                                    aria-label="Remaining post analysis period"
                                    style={{ width: '140px' }}
                                >
                                    <option value="1month">1 Month</option>
                                    <option value="2months">2 Months</option>
                                    <option value="3months">3 Months</option>
                                    <option value="6months">6 Months</option>
                                    <option value="1year">1 Year</option>
                                    <option value="all">All Times</option>
                                </Form.Select>
                                {nextClosedTradeState.error && (
                                    <span className="text-danger small">{nextClosedTradeState.error}</span>
                                )}
                                {remainingPostAnalysisState.error && (
                                    <span className="text-danger small">{remainingPostAnalysisState.error}</span>
                                )}
                                {deleteTradeState.error && (
                                    <span className="text-danger small">{deleteTradeState.error}</span>
                                )}
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleLoadPreviousClosedTrade}
                                    disabled={prevClosedTradeState.loading || prevClosedTradeState.hasPrev === false}
                                >
                                    {prevClosedTradeState.loading ? 'Loading...' : 'Prev Closed Trade'}
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleLoadNextClosedTrade}
                                    disabled={nextClosedTradeState.loading}
                                >
                                    {nextClosedTradeState.loading ? 'Loading...' : 'Next Closed Trade'}
                                </Button>
                                {(trade.transactions || []).some(tx => tx.transaction_type === 'BUY') &&
                                    !(trade.transactions || []).some(tx => tx.transaction_type === 'SELL') && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={handleDeleteOrphanTrade}
                                        disabled={deleteTradeState.loading}
                                    >
                                        {deleteTradeState.loading ? 'Deleting...' : 'Delete Orphan Trade'}
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                </div>
                {showBuySlots && (uniquePrices.length === 0 ? (
                    <div className="text-center py-4">
                        <p className="text-muted">No purchase transactions found</p>
                    </div>
                ) : (
                    <div className="row g-3">
                        {uniquePrices.map((priceGroup, index) => (
                            <div key={index} className="col-12">
                                <Card className="trade-details-card mb-3">
                                    <Card.Header className="bg-light">
                                        <div className="d-flex align-items-center">
                                            <h6 className="mb-0">
                                                Purchase Price: {formatCurrency(priceGroup.price)} × {(() => {
                                                    const totalShares = priceGroup.totalShares || 0;
                                                    const remaining = priceGroup.remainingShares ?? totalShares;
                                                    if (isEffectivelyZero(remaining)) {
                                                        return `Sold (${totalShares.toLocaleString()})`;
                                                    }
                                                    if (Math.abs(remaining - totalShares) < SHARES_TOLERANCE) {
                                                        return `${totalShares.toLocaleString()} shares`;
                                                    }
                                                    return `Holding (${Math.round(remaining).toLocaleString()}/${Math.round(totalShares).toLocaleString()})`;
                                                })()} = {formatCurrency(priceGroup.totalAmount)}
                                            </h6>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        {trade && trade.account && (
                                            <>
                                                <ArmorEdit
                                                    accountId={trade.account.account_id}
                                                    tickerId={trade.ticker_id}
                                                    currentArmor={trade.shield_count || 0}
                                                    inline
                                                    enableRealtimeUpdates={false}
                                                    onShieldUpdate={fetchTradeDetails}
                                                    customCostBasis={priceGroup.price}
                                                    customShares={trade.status === 'CLOSED' ? priceGroup.totalShares : priceGroup.remainingShares}
                                                    transactionId={priceGroup.transactions[0]?.transaction_id}
                                                    hasOverallShield={hasOverallShield}
                                                    isSoldOut={priceGroup.isSoldOut}
                                                    isClosed={trade.status === 'CLOSED'}
                                                    soldPrice={(() => {
                                                        if (trade.status !== 'CLOSED') return null;
                                                        const sells = (trade.transactions || []).filter(tx => tx.transaction_type === 'SELL');
                                                        if (sells.length === 0) return null;
                                                        const { totalProceeds, totalSharesSold } = sells.reduce((acc, tx) => {
                                                            const shares = Number(tx.shares) || 0;
                                                            const proceeds = shares * (Number(tx.price_per_share) || 0);
                                                            return {
                                                                totalProceeds: acc.totalProceeds + proceeds,
                                                                totalSharesSold: acc.totalSharesSold + shares
                                                            };
                                                        }, { totalProceeds: 0, totalSharesSold: 0 });
                                                        if (totalSharesSold <= 0) return null;
                                                        return totalProceeds / totalSharesSold;
                                                    })()}
                                                    closedRightContent={trade.status === 'CLOSED' && index === 0 ? renderPostAnalysis() : null}
                                                    // Pass shields data directly for closed trades
                                                    shields={trade.status === 'CLOSED' ? (trade.shields || []) : undefined}
                                                    tickerInfo={trade.status === 'CLOSED' ? {
                                                        symbol: trade.ticker?.symbol,
                                                        name: trade.ticker?.name,
                                                        current_price: (() => {
                                                            // For closed trades, use the final sell price as the "current" price
                                                            if (trade.status !== 'CLOSED') return trade.ticker?.current_price;
                                                            const sells = (trade.transactions || []).filter(tx => tx.transaction_type === 'SELL');
                                                            if (sells.length === 0) return trade.ticker?.current_price;
                                                            const { totalProceeds, totalSharesSold } = sells.reduce((acc, tx) => {
                                                                const shares = Number(tx.shares) || 0;
                                                                const proceeds = shares * (Number(tx.price_per_share) || 0);
                                                                return {
                                                                    totalProceeds: acc.totalProceeds + proceeds,
                                                                    totalSharesSold: acc.totalSharesSold + shares
                                                                };
                                                            }, { totalProceeds: 0, totalSharesSold: 0 });
                                                            return totalSharesSold > 0 ? totalProceeds / totalSharesSold : trade.ticker?.current_price;
                                                        })(),
                                                        cost_basis: priceGroup.price,
                                                        shares: priceGroup.totalShares
                                                    } : undefined}
                                                    // Pass hit price information for trailing stops
                                                    hitPrices={trade.status === 'CLOSED' ? (trade.shields || []).reduce((acc, shield) => {
                                                        if (shield.shield_type === 'TRAILING_STOP' && shield.hit_price) {
                                                            acc[shield.shield_id] = {
                                                                hit_price: shield.hit_price,
                                                                hit_timestamp: shield.hit_timestamp
                                                            };
                                                        }
                                                        return acc;
                                                    }, {}) : undefined}
                                                />
                                            </>
                                        )}
                                    </Card.Body>
                                </Card>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );

        // Return the integrated section; Post Analysis is injected within when CLOSED
        return leftContent;
    };

    const handleEditPostAnalysis = () => {
        const lessonState = parseLessonRatings(trade.post_analysis_lessons || '');
        setPostAnalysisDraft({
            post_analysis_rating: getRatingFromLessons(lessonState.ratings),
            post_analysis_analysis: trade.post_analysis_analysis || '',
            post_analysis_lessons: buildLessonsText(lessonState.ratings, lessonState.freeText)
        });
        setLessonRatings(lessonState.ratings);
        setLessonsFreeText(lessonState.freeText);
        setEditingPostAnalysis(true);
    };

    const handleCancelPostAnalysis = () => {
        setEditingPostAnalysis(false);
        const lessonState = parseLessonRatings(trade.post_analysis_lessons || '');
        setPostAnalysisDraft({
            post_analysis_rating: getRatingFromLessons(lessonState.ratings),
            post_analysis_analysis: trade.post_analysis_analysis || '',
            post_analysis_lessons: buildLessonsText(lessonState.ratings, lessonState.freeText)
        });
        setLessonRatings(lessonState.ratings);
        setLessonsFreeText(lessonState.freeText);
    };

    const handleSavePostAnalysis = async () => {
        const saveTradeId = tradeId;
        try {
            setSavingPostAnalysis(true);
            await updateTrade(saveTradeId, postAnalysisDraft);
            if (latestTradeIdRef.current === saveTradeId) {
                const lessonState = parseLessonRatings(postAnalysisDraft.post_analysis_lessons || '');
                await fetchTradeDetails();
                await fetchRemainingPostAnalysisCount();
                await refreshPrevClosedTradeAvailability();
                setLessonRatings(lessonState.ratings);
                setLessonsFreeText(lessonState.freeText);
                setEditingPostAnalysis(false);
            }
        } catch (err) {
            console.error('Error saving post analysis:', err);
            setError('Failed to save post analysis');
            setTimeout(() => setError(''), 3000);
        } finally {
            setSavingPostAnalysis(false);
        }
    };

    function parseLessonRatings(lessonsText) {
        const ratings = { entry: null, timing: null, stop: null, size: null, execution: null };
        if (!lessonsText) {
            return { ratings, freeText: '' };
        }

        const lines = lessonsText.split('\n');
        const freeLines = [];

        lines.forEach((line) => {
            const match = line.match(/^\s*(Entry|Timing|Stop|Size|Execution)\s*:\s*(Good|Bad)\s*$/i);
            if (match) {
                const key = match[1].toLowerCase().replace(/\s+/g, '_');
                const value = match[2].charAt(0).toUpperCase() + match[2].slice(1).toLowerCase();
                ratings[key] = value;
            } else {
                freeLines.push(line);
            }
        });

        const freeText = freeLines.join('\n').replace(/^\n+/, '');
        return { ratings, freeText };
    }

    function buildLessonsText(ratings, freeText) {
        const lines = [];
        if (ratings.entry) lines.push(`Entry: ${ratings.entry}`);
        if (ratings.timing) lines.push(`Timing: ${ratings.timing}`);
        if (ratings.stop) lines.push(`Stop: ${ratings.stop}`);
        if (ratings.size) lines.push(`Size: ${ratings.size}`);
        if (ratings.execution) lines.push(`Execution: ${ratings.execution}`);

        const header = lines.join('\n');
        const trimmedFreeText = (freeText || '').trim();

        if (header && trimmedFreeText) {
            return `${header}\n\n${trimmedFreeText}`;
        }
        if (header) {
            return header;
        }
        return trimmedFreeText;
    }

    const getRatingFromLessons = (ratings) => {
        const goodCount = Object.values(ratings).filter((value) => value === 'Good').length;
        return goodCount > 0 ? goodCount : null;
    };

    const handleLessonRatingChange = (key, value) => {
        if (!editingPostAnalysis) {
            return;
        }
        const nextRatings = {
            ...lessonRatings,
            [key]: lessonRatings[key] === value ? null : value
        };
        const nextRatingValue = getRatingFromLessons(nextRatings);
        setLessonRatings(nextRatings);
        setPostAnalysisDraft((prev) => ({
            ...prev,
            post_analysis_rating: nextRatingValue,
            post_analysis_lessons: buildLessonsText(nextRatings, lessonsFreeText)
        }));
    };

    const isPostAnalysisDirty = () => {
        if (!trade) {
            return false;
        }

        const normalizeText = (value) => (value || '').trim();
        const draftRating = postAnalysisDraft.post_analysis_rating ?? null;
        const tradeRating = trade.post_analysis_rating ?? null;

        return (
            draftRating !== tradeRating ||
            normalizeText(postAnalysisDraft.post_analysis_analysis) !== normalizeText(trade.post_analysis_analysis) ||
            normalizeText(postAnalysisDraft.post_analysis_lessons) !== normalizeText(trade.post_analysis_lessons)
        );
    };

    const fetchRemainingPostAnalysisCount = async () => {
        if (remainingPostAnalysisState.loading) {
            return;
        }

        setRemainingPostAnalysisState({ loading: true, error: '', count: null });

        const { startDate, endDate } = getPostAnalysisDateRange(remainingPostAnalysisPeriod);

        try {
            const response = await getTrades({
                status: 'closed',
                post_analysis_filter: 'empty',
                page: 1,
                per_page: 1,
                sort_by: 'closed_at',
                sort_order: 'desc',
                start_date: startDate ? formatDateForApi(startDate) : undefined,
                end_date: endDate ? formatDateForApi(endDate) : undefined
            });

            const counts = response.data?.post_analysis_counts;
            const total = counts?.empty ?? response.data?.pagination?.total ?? 0;

            setRemainingPostAnalysisState({ loading: false, error: '', count: total });
        } catch (err) {
            console.error('Error loading remaining post analysis count:', err);
            setRemainingPostAnalysisState({
                loading: false,
                error: 'Unable to load remaining count.',
                count: null
            });
        }
    };

    const fetchAdjacentClosedTrade = async (direction) => {
        const { startDate, endDate } = getPostAnalysisDateRange(remainingPostAnalysisPeriod);
        const response = await getAdjacentUnanalyzedTrade({
            current_trade_id: trade.trade_id || tradeId,
            direction,
            start_date: startDate ? formatDateForApi(startDate) : undefined,
            end_date: endDate ? formatDateForApi(endDate) : undefined
        });
        return response.data?.trade || null;
    };

    const refreshPrevClosedTradeAvailability = async () => {
        if (!trade || trade.status !== 'CLOSED' || prevClosedTradeState.loading) {
            return;
        }

        setPrevClosedTradeState({ loading: true, error: '', hasPrev: null });

        try {
            const tradeSummary = await fetchAdjacentClosedTrade('prev');
            setPrevClosedTradeState({
                loading: false,
                error: '',
                hasPrev: Boolean(tradeSummary)
            });
        } catch (err) {
            console.error('Error checking previous closed trade:', err);
            setPrevClosedTradeState({
                loading: false,
                error: 'Unable to check previous trade.',
                hasPrev: null
            });
        }
    };

    const handleLoadNextClosedTrade = async () => {
        if (!trade || trade.status !== 'CLOSED' || nextClosedTradeState.loading) {
            return;
        }

        setNextClosedTradeState({ loading: true, error: '' });
        const navigateFromTradeId = tradeId;

        try {
            if (isPostAnalysisDirty() && !savingPostAnalysis) {
                setSavingPostAnalysis(true);
                await updateTrade(navigateFromTradeId, postAnalysisDraft);
                if (latestTradeIdRef.current === navigateFromTradeId) {
                    setTrade((prevTrade) => {
                        if (!prevTrade) {
                            return prevTrade;
                        }
                        return {
                            ...prevTrade,
                            post_analysis_rating: postAnalysisDraft.post_analysis_rating,
                            post_analysis_analysis: postAnalysisDraft.post_analysis_analysis,
                            post_analysis_lessons: postAnalysisDraft.post_analysis_lessons
                        };
                    });
                }
            }

            const tradeSummary = await fetchAdjacentClosedTrade('next');
            if (!tradeSummary) {
                setNextClosedTradeState({
                    loading: false,
                    error: 'No more closed trades without post analysis.'
                });
                return;
            }

            setNextClosedTradeState({ loading: false, error: '' });
            navigate(`/trades/${tradeSummary.trade_id}`);
        } catch (err) {
            console.error('Error loading next closed trade:', err);
            setNextClosedTradeState({
                loading: false,
                error: 'Failed to load the next closed trade.'
            });
        } finally {
            setSavingPostAnalysis(false);
        }
    };

    const handleLoadPreviousClosedTrade = async () => {
        if (!trade || trade.status !== 'CLOSED' || prevClosedTradeState.loading) {
            return;
        }

        setPrevClosedTradeState({ loading: true, error: '', hasPrev: prevClosedTradeState.hasPrev });

        try {
            const tradeSummary = await fetchAdjacentClosedTrade('prev');
            if (!tradeSummary) {
                setPrevClosedTradeState({
                    loading: false,
                    error: '',
                    hasPrev: false
                });
                return;
            }

            setPrevClosedTradeState({ loading: false, error: '', hasPrev: true });
            navigate(`/trades/${tradeSummary.trade_id}`);
        } catch (err) {
            console.error('Error loading previous closed trade:', err);
            setPrevClosedTradeState({
                loading: false,
                error: 'Failed to load the previous closed trade.',
                hasPrev: prevClosedTradeState.hasPrev
            });
        }
    };

    const handleDeleteOrphanTrade = async () => {
        if (!trade || deleteTradeState.loading) {
            return;
        }

        const confirmed = window.confirm(
            'Delete this closed trade with no sell transactions? This cannot be undone.'
        );
        if (!confirmed) {
            return;
        }

        setDeleteTradeState({ loading: true, error: '' });
        try {
            const nextTrade = await fetchAdjacentClosedTrade('next');
            await deleteTrade(trade.trade_id);
            setDeleteTradeState({ loading: false, error: '' });
            if (nextTrade?.trade_id) {
                navigate(`/trades/${nextTrade.trade_id}`);
            } else {
                navigate('/trades');
            }
        } catch (err) {
            console.error('Error deleting trade:', err);
            setDeleteTradeState({
                loading: false,
                error: 'Failed to delete trade.'
            });
        }
    };

    const handleAddToWatchlist = async () => {
        if (!trade?.ticker_symbol || watchlistAddState.loading) {
            return;
        }
        setWatchlistAddState({ loading: true, error: '', success: false });
        try {
            await addWatchlistTicker(trade.ticker_symbol);
            setWatchlistAddState({ loading: false, error: '', success: true });
            setTimeout(() => {
                setWatchlistAddState(prev => ({ ...prev, success: false }));
            }, 2000);
        } catch (err) {
            const errorMessage = err?.response?.data?.error || '';
            if (errorMessage.toLowerCase().includes('already in watchlist')) {
                setWatchlistAddState({ loading: false, error: '', success: true });
                setTimeout(() => {
                    setWatchlistAddState(prev => ({ ...prev, success: false }));
                }, 2000);
                return;
            }
            setWatchlistAddState({
                loading: false,
                error: errorMessage || 'Failed to add to watchlist.',
                success: false
            });
        }
    };

    const renderPostAnalysis = () => {
        if (!trade || trade.status !== 'CLOSED') return null;
        const goodRatingsCount = Object.values(lessonRatings).filter((value) => value === 'Good').length;
        const lessonDisplay = parseLessonRatings(trade.post_analysis_lessons || '').freeText;
        return (
            <Card className="trade-details-card mb-3 d-flex flex-column">
                <Card.Header className="bg-light">
                    <div className="d-flex align-items-center">
                        <div className="fw-bold">Post Analysis</div>
                        <div className="flex-grow-1 text-center">
                            {[1,2,3,4,5].map((star) => (
                                <i
                                    key={star}
                                    className={`bi ${star <= goodRatingsCount ? 'bi-star-fill text-warning' : 'bi-star text-muted'} fs-5 me-1`}
                                    style={{ cursor: 'default' }}
                                    title="Rating is based on analysis grading"
                                />
                            ))}
                        </div>
                        {!editingPostAnalysis ? (
                            <Button variant="outline-primary" size="sm" onClick={handleEditPostAnalysis}>
                                Edit
                            </Button>
                        ) : (
                            <div className="d-flex gap-2">
                                <Button variant="secondary" size="sm" onClick={handleCancelPostAnalysis} disabled={savingPostAnalysis}>
                                    Cancel
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleSavePostAnalysis} disabled={savingPostAnalysis}>
                                    {savingPostAnalysis ? (
                                        <>
                                            <Spinner animation="border" size="sm" role="status" className="me-2">
                                                <span className="visually-hidden">Saving...</span>
                                            </Spinner>
                                            Saving...
                                        </>
                                    ) : 'Save'}
                                </Button>
                            </div>
                        )}
                    </div>
                </Card.Header>
                <Card.Body className="d-flex flex-column">
                    <div className="row g-3 flex-grow-1">
                        <div className="col-md-4 d-flex flex-column">
                            <div className="mb-2 fw-semibold">Analysis</div>
                            <div className="mb-3">
                                <Table borderless size="sm" className="mb-0 align-middle">
                                    <tbody>
                                        {LESSON_RATING_FIELDS.map((field) => (
                                            <tr key={field.key} className="border-bottom">
                                                <td className="fw-semibold text-dark">{field.label}</td>
                                                <td className="text-end">
                                                    <Button
                                                        variant={lessonRatings[field.key] === 'Good' ? 'success' : 'outline-success'}
                                                        size="sm"
                                                        onClick={() => handleLessonRatingChange(field.key, 'Good')}
                                                        disabled={!editingPostAnalysis}
                                                        aria-label={`${field.label} good`}
                                                    >
                                                        <i className="bi bi-check-lg"></i>
                                                    </Button>
                                                </td>
                                                <td className="text-end">
                                                    <Button
                                                        variant={lessonRatings[field.key] === 'Bad' ? 'danger' : 'outline-danger'}
                                                        size="sm"
                                                        onClick={() => handleLessonRatingChange(field.key, 'Bad')}
                                                        disabled={!editingPostAnalysis}
                                                        aria-label={`${field.label} bad`}
                                                    >
                                                        <i className="bi bi-x-lg"></i>
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                            <div className="p-2 rounded overflow-auto">
                                {trade.post_analysis_analysis && (
                                    <div style={{ whiteSpace: 'pre-wrap' }}>{trade.post_analysis_analysis}</div>
                                )}
                            </div>
                        </div>
                        <div className="col-md-8 d-flex flex-column">
                            <div className="mb-2 fw-semibold">Lessons</div>
                            {!editingPostAnalysis ? (
                            <div className="p-2 border rounded">
                                {lessonDisplay ? (
                                    <div style={{ whiteSpace: 'pre-wrap' }}>{lessonDisplay}</div>
                                ) : (
                                    <span className="text-muted">No lessons captured yet.</span>
                                )}
                            </div>
                            ) : (
                                <div className="d-flex flex-grow-1">
                                    <textarea
                                        className="form-control"
                                        style={{ flex: 1 }}
                                        value={lessonsFreeText}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setLessonsFreeText(value);
                                            setPostAnalysisDraft((prev) => ({
                                                ...prev,
                                                post_analysis_lessons: buildLessonsText(lessonRatings, value)
                                            }));
                                        }}
                                        placeholder="What will you do better next time? Rules, signals, context, risk, size..."
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    if (loading) {
        return (
            <Container className="mt-1">
                <div className="text-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-3 text-muted">Loading trade details...</p>
                </div>
            </Container>
        );
    }

    if (error || !trade) {
        return (
            <Container className="mt-1">
                <Alert variant="danger">
                    {error || 'Trade not found'}
                </Alert>
                <div className="text-center mt-3">
                    <Button variant="primary" onClick={() => navigate('/trades')}>
                        Back to Trades
                    </Button>
                </div>
            </Container>
        );
    }

    return (
        <>
            <style>{customStyles}</style>
            <Modal show={showTickerModal} onHide={handleCloseTickerModal} centered>
                <Modal.Header closeButton={!tickerLookupState.loading}>
                    <Modal.Title>Load Active Trade</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                        <Form.Group controlId="tickerSymbolInput">
                            <Form.Label>Enter Ticker Symbol</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., AAPL"
                                value={tickerInput}
                                autoFocus
                                onChange={(e) => handleTickerInputChange(e.target.value)}
                                onKeyDown={handleTickerInputKeyDown}
                            />
                        <Form.Text muted>
                            Select a trade status below to search active or closed positions.
                        </Form.Text>
                        </Form.Group>
                        <Form.Group className="mt-3">
                            <Form.Label className="d-block mb-2">Trade Status</Form.Label>
                            <div className="d-flex gap-3">
                                <Form.Check
                                    inline
                                    type="radio"
                                    id="ticker-status-active"
                                    name="ticker-status"
                                    label="Active"
                                    value="active"
                                    checked={tickerSearchStatus === 'active'}
                                    onChange={() => {
                                        setTickerSearchStatus('active');
                                        setMatchingTrades([]);
                                    }}
                                    disabled={tickerLookupState.loading}
                                />
                                <Form.Check
                                    inline
                                    type="radio"
                                    id="ticker-status-closed"
                                    name="ticker-status"
                                    label="Closed"
                                    value="closed"
                                    checked={tickerSearchStatus === 'closed'}
                                    onChange={() => {
                                        setTickerSearchStatus('closed');
                                        setMatchingTrades([]);
                                    }}
                                    disabled={tickerLookupState.loading}
                                />
                            </div>
                        </Form.Group>
                        {matchingTrades.length > 1 && (
                            <div className="mt-3">
                                <Alert variant="info" className="mb-2">
                                    Multiple {tickerSearchStatus} trades found for {tickerInput || 'this symbol'}. Select one to view details.
                                </Alert>
                                <div className="list-group">
                                    {matchingTrades.map(match => (
                                        <button
                                            key={match.trade_id}
                                            type="button"
                                            className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                            onClick={() => handleTradeSelection(match)}
                                        >
                                            <div>
                                                <div className="fw-bold">{match.ticker_symbol} · #{match.trade_id}</div>
                                                <small className="text-muted">
                                                    {match.account_name || 'Unknown Account'} · {match.status}
                                                </small>
                                            </div>
                                            <div className="text-end">
                                                <div className="small text-muted">
                                                    Started {match.created_at ? formatDateWithWeekday(match.created_at) : 'N/A'}
                                                </div>
                                                {match.total_shares && (
                                                    <div className="small">
                                                        {Number(match.total_shares).toLocaleString()} shares
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {tickerLookupState.error && (
                            <Alert variant="danger" className="mt-3 mb-0">
                                {tickerLookupState.error}
                            </Alert>
                        )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseTickerModal} disabled={tickerLookupState.loading}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => handleTickerLookup(tickerInput)}
                        disabled={
                            tickerLookupState.loading ||
                            !tickerInput.trim() ||
                            tickerInput.trim().toUpperCase() === (trade?.ticker_symbol || '').toUpperCase()
                        }
                    >
                        {tickerLookupState.loading ? (
                            <Spinner animation="border" size="sm" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        ) : 'Load Trade'}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Container className="mt-1">
                {/* Error Message */}
                {error && (
                    <Row className="mb-3">
                        <Col>
                            <Alert variant="danger">{error}</Alert>
                        </Col>
                    </Row>
                )}



                {/* Integrated Ticker Row with All Stats */}
                <Row className="mb-4">
                    <Col>
                        <Card className="trade-details-card">
                            <Card.Body className="p-3 ps-4">
                                <div className="row align-items-center gx-2">
                                    {/* Ticker Info */}
                                    <div className="col-lg-3 col-md-4">
                                        <div className="d-flex align-items-center">
                                            <div>
                                                <div className="d-flex align-items-center mb-1 flex-wrap">
                                                    <div 
                                                        className="d-flex align-items-center me-3 mb-2" 
                                                        style={{ width: 'auto', minWidth: '190px', maxWidth: '280px', gap: '0.5rem' }}
                                                    >
                                                        <button 
                                                            type="button" 
                                                            className="ticker-symbol-button"
                                                            onClick={handleOpenTickerModal}
                                                            aria-label="Change ticker"
                                                        >
                                                            <span className="ticker-symbol-text">{trade.ticker_symbol}</span>
                                                        </button>
                                                        <Badge
                                                            bg={trade.status === 'ACTIVE'
                                                                ? 'success'
                                                                : (trade.final_gain_loss ?? 0) >= 0 ? 'success' : 'danger'}
                                                            className="fs-6"
                                                        >
                                                            {trade.status}
                                                        </Badge>
                                                        <Badge
                                                            bg={(trade.status === 'CLOSED'
                                                                ? (trade.final_gain_loss_pct ?? trade.total_gain_loss_pct ?? 0)
                                                                : (trade.total_gain_loss_pct ?? 0)
                                                            ) >= 0 ? 'success' : 'danger'}
                                                            className="fs-6"
                                                        >
                                                            {trade.status === 'CLOSED'
                                                                ? formatPercentage(trade.final_gain_loss_pct ?? trade.total_gain_loss_pct)
                                                                : formatPercentage(trade.total_gain_loss_pct)}
                                                        </Badge>
                                                    </div>
                                                    {trade.status !== 'CLOSED' && (() => {
                                                        const totalValue = (trade.total_shares || 0) * (trade.current_price || 0);
                                                        const gainValue = trade.total_gain_loss || 0;
                                                        return (
                                                        <div className="mb-2">
                                                            <span 
                                                                className={`fw-bold ${getGainLossTextColor(trade.total_gain_loss)}`} 
                                                                style={{ fontSize: '1.5rem', letterSpacing: '0.01em' }}
                                                            >
                                                                {formatCurrencyNoCents(totalValue)} ({gainValue < 0 ? '-' : ''}{formatCurrencyNoCents(Math.abs(gainValue))})
                                                            </span>
                                                        </div>
                                                        );
                                                    })()}
                                                    {trade.status === 'CLOSED' && (() => {
                                                        const sellTransactions = (trade.transactions || []).filter(tx => tx.transaction_type === 'SELL');
                                                        const totalValue = sellTransactions.reduce((sum, tx) => {
                                                            const shares = Number(tx.shares) || 0;
                                                            const price = Number(tx.price_per_share) || 0;
                                                            return sum + (shares * price);
                                                        }, 0);
                                                        const gainValue = trade.final_gain_loss ?? trade.total_gain_loss ?? 0;
                                                        return (
                                                        <div className="mb-2">
                                                            <span
                                                                className={`fw-bold ${getGainLossTextColor(gainValue)}`}
                                                                style={{ fontSize: '1.5rem', letterSpacing: '0.01em' }}
                                                            >
                                                                {formatCurrencyNoCents(totalValue)} ({gainValue < 0 ? '-' : ''}{formatCurrencyNoCents(Math.abs(gainValue))})
                                                            </span>
                                                        </div>
                                                        );
                                                    })()}
                                                </div>
                                                    {trade.account && (
                                                    <div className="mt-1 d-flex align-items-center flex-wrap gap-2">
                                                        <small className="text-muted">
                                                            <i className="bi bi-bank me-1"></i>
                                                            <Link 
                                                                to={`/account/${trade.account.account_id}`}
                                                                className="text-decoration-none text-primary"
                                                                title="Go to Account Details"
                                                            >
                                                                {trade.account.account_name}
                                                            </Link>
                                                            {trade.account.portfolio_name && (
                                                                <span className="ms-1">({trade.account.portfolio_name})</span>
                                                            )}
                                                        </small>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                                            className="py-0"
                                                            onClick={handleAddToWatchlist}
                                                            disabled={watchlistAddState.loading}
                                                        >
                                                            {watchlistAddState.loading ? 'Adding...' : '+Watch'}
                                                        </Button>
                                                        {watchlistAddState.success && (
                                                            <span className="text-success small">Added</span>
                                                        )}
                                                        {watchlistAddState.error && (
                                                            <span className="text-danger small">{watchlistAddState.error}</span>
                                                        )}
                                                    </div>
                                                    )}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Started Date */}
                                    <div className="col-6 col-lg text-center">
                                        <div className="mb-1">
                                            <span className="h6 mb-0 fw-bold text-info">
                                                {formatDateWithWeekday(trade.created_at)}
                                            </span>
                                        </div>
                                        <small className="text-muted">Started</small>
                                    </div>
                                    
                                    {/* Total Shares */}
                                    <div className="col-6 col-lg text-center">
                                        <div className="mb-1">
                                            <span className="h6 mb-0 fw-bold text-primary">
                                                {trade.total_shares?.toLocaleString() || '0'}
                                            </span>
                                        </div>
                                        <small className="text-muted">Shares</small>
                                    </div>
                                    

                                    
                                    {/* Today's Gain */}
                                    <div className="col-6 col-lg text-center">
                                        <div className="mb-1">
                                            <span className={`h6 mb-0 fw-bold text-${getGainLossColor(trade.today_gain_amount)}`}>
                                                {trade.today_gain_amount ? formatCurrency(trade.today_gain_amount) : 'N/A'}
                                            </span>
                                        </div>
                                        <small className="text-muted">Today $</small>
                                    </div>
                                    
                                    {/* Today's % */}
                                    <div className="col-6 col-lg text-center">
                                        <div className="mb-1">
                                            <span className={`h6 mb-0 fw-bold text-${getGainLossColor(trade.today_gain_percentage)}`}>
                                                {trade.today_gain_percentage ? formatPercentage(trade.today_gain_percentage) : 'N/A'}
                                            </span>
                                        </div>
                                        <small className="text-muted">Today %</small>
                                    </div>
                                    
                                    {/* Total Gain */}
                                    <div className="col-6 col-lg text-center">
                                        <div className="mb-1">
                                            <span className={`h6 mb-0 fw-bold text-${getGainLossColor(trade.total_gain_loss)}`}>
                                                {formatCurrency(trade.total_gain_loss)}
                                            </span>
                                        </div>
                                        <small className="text-muted">Total $</small>
                                    </div>
                                    
                                    {/* Total % */}
                                    <div className="col-6 col-lg text-center">
                                        <div className="mb-1">
                                            <span className={`h6 mb-0 fw-bold text-${getGainLossColor(trade.total_gain_loss_pct)}`}>
                                                {formatPercentage(trade.total_gain_loss_pct)}
                                            </span>
                                        </div>
                                        <small className="text-muted">Total %</small>
                                    </div>
                                    
                                    {/* Days Held */}
                                    <div className="col-6 col-lg text-center">
                                        <div className="mb-1">
                                            <span className="h6 mb-0 fw-bold text-warning">
                                                {trade.days_held || 0}
                                            </span>
                                        </div>
                                        <small className="text-muted">Days</small>
                                    </div>
                                    
                                    {/* Shields Count */}
                                    <div className="col-6 col-lg text-center">
                                        <div className="mb-1">
                                            <span className={`h6 mb-0 fw-bold ${(trade.shields?.length || 0) > 0 ? 'text-success' : 'text-muted'}`}>
                                                <i className="bi bi-shield-check me-1"></i>
                                                {trade.shields?.length || 0}
                                            </span>
                                        </div>
                                        <small className="text-muted">Shields</small>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Gains Summary Section */}
                <Row className="mb-4">
                    <Col>
                        <Card className="trade-details-card">
                            <Card.Body className="p-3">
                                <div className="row text-center">
                                    {/* Realized Gain */}
                                    <div className="col">
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip id="realized-gain-tooltip" style={{
                                                    '--bs-tooltip-bg': 'white',
                                                    '--bs-tooltip-color': '#212529',
                                                    '--bs-tooltip-max-width': '1200px'
                                                }}>
                                                    <div style={{ textAlign: 'left', color: '#212529' }}>
                                                        <strong>Realized Gain Calculation:</strong>
                                                        {(() => {
                                                            const sellTransactions = trade.transactions?.filter(tx => tx.transaction_type === 'SELL') || [];
                                                            const totalProceeds = sellTransactions.reduce((sum, tx) => sum + Number(tx.total_amount || 0), 0);
                                                            const totalCostBasis = totalProceeds - (trade.realized_gain || 0);
                                                            const realizedGain = totalProceeds - totalCostBasis;

                                                            return (
                                                                <>
                                                                    <div className="mt-1 text-muted small">
                                                                        From {sellTransactions.length} SELL transaction{sellTransactions.length !== 1 ? 's' : ''}
                                                                    </div>
                                                                    <div className="mt-1">
                                                                        Sale Proceeds: {formatCurrency(totalProceeds)}
                                                                    </div>
                                                                    <div>
                                                                        Cost Basis: {formatCurrency(totalCostBasis)}
                                                                    </div>
                                                                    <div className="mt-1 pt-1" style={{ borderTop: '1px solid #dee2e6', whiteSpace: 'nowrap' }}>
                                                                        <strong>{formatCurrency(totalProceeds)} - {formatCurrency(totalCostBasis)} = <span className={realizedGain >= 0 ? 'text-success' : 'text-danger'}>{formatCurrency(realizedGain)}</span></strong>
                                                                    </div>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                </Tooltip>
                                            }
                                        >
                                            <div style={{ cursor: 'help' }}>
                                                <div className={`fw-bold h5 text-${getGainLossColor(trade.realized_gain)}`}>
                                                    {formatCurrency(trade.realized_gain)}
                                                    <span className="ms-2 small text-muted">({formatPercentage(trade.realized_gain_pct)})</span>
                                                </div>
                                                <div className="text-muted" style={{ borderBottom: '1px dotted currentColor', display: 'inline-block' }}>
                                                    Realized Gain
                                                </div>
                                            </div>
                                        </OverlayTrigger>
                                    </div>
                                    {/* Unrealized Gain */}
                                    <div className="col">
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip id="unrealized-gain-tooltip" style={{
                                                    '--bs-tooltip-bg': 'white',
                                                    '--bs-tooltip-color': '#212529',
                                                    '--bs-tooltip-max-width': '1200px'
                                                }}>
                                                    <div style={{ textAlign: 'left', color: '#212529' }}>
                                                        <strong>Unrealized Gain Calculation:</strong>
                                                        {(() => {
                                                            const sharesHeld = trade.total_shares || 0;
                                                            const currentPrice = trade.current_price || 0;
                                                            const avgCostBasis = trade.total_cost_basis || 0;
                                                            const currentValue = sharesHeld * currentPrice;
                                                            const totalCostBasis = sharesHeld * avgCostBasis;
                                                            const unrealizedGain = currentValue - totalCostBasis;

                                                            return (
                                                                <>
                                                                    <div className="mt-1">
                                                                        Current Value = {sharesHeld.toLocaleString()} shares × {formatCurrency(currentPrice)}
                                                                    </div>
                                                                    <div className="text-end">
                                                                        = {formatCurrency(currentValue)}
                                                                    </div>
                                                                    <div className="mt-1">
                                                                        Cost Basis = {sharesHeld.toLocaleString()} shares × {formatCurrency(avgCostBasis)}
                                                                    </div>
                                                                    <div className="text-end">
                                                                        = {formatCurrency(totalCostBasis)}
                                                                    </div>
                                                                    <div className="mt-1 pt-1" style={{ borderTop: '1px solid #dee2e6', whiteSpace: 'nowrap' }}>
                                                                        <strong>{formatCurrency(currentValue)} - {formatCurrency(totalCostBasis)} = <span className={unrealizedGain >= 0 ? 'text-success' : 'text-danger'}>{formatCurrency(unrealizedGain)}</span></strong>
                                                                    </div>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                </Tooltip>
                                            }
                                        >
                                            <div style={{ cursor: 'help' }}>
                                                <div className={`fw-bold h5 text-${getGainLossColor(trade.unrealized_gain)}`}>
                                                    {formatCurrency(trade.unrealized_gain)}
                                                    <span className="ms-2 small text-muted">({formatPercentage(trade.unrealized_gain_pct)})</span>
                                                </div>
                                                <div className="text-muted" style={{ borderBottom: '1px dotted currentColor', display: 'inline-block' }}>
                                                    Unrealized Gain
                                                </div>
                                            </div>
                                        </OverlayTrigger>
                                    </div>
                                    {/* Total Gain */}
                                    <div className="col">
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip id="total-gain-tooltip" style={{
                                                    '--bs-tooltip-bg': 'white',
                                                    '--bs-tooltip-color': '#212529',
                                                    '--bs-tooltip-max-width': '1200px'
                                                }}>
                                                    <div style={{ textAlign: 'left', color: '#212529' }}>
                                                        <strong>Total Gain Calculation:</strong>
                                                        {(() => {
                                                            const realizedGain = trade.realized_gain || 0;
                                                            const unrealizedGain = trade.unrealized_gain || 0;
                                                            const totalGain = realizedGain + unrealizedGain;

                                                            return (
                                                                <>
                                                                    <div className="mt-1">
                                                                        Realized: <span className={realizedGain >= 0 ? 'text-success' : 'text-danger'}>{formatCurrency(realizedGain)}</span>
                                                                    </div>
                                                                    <div>
                                                                        Unrealized: <span className={unrealizedGain >= 0 ? 'text-success' : 'text-danger'}>{formatCurrency(unrealizedGain)}</span>
                                                                    </div>
                                                                    <div className="mt-1 pt-1" style={{ borderTop: '1px solid #dee2e6', whiteSpace: 'nowrap' }}>
                                                                        <strong>{formatCurrency(realizedGain)} + {formatCurrency(unrealizedGain)} = <span className={totalGain >= 0 ? 'text-success' : 'text-danger'}>{formatCurrency(totalGain)}</span></strong>
                                                                    </div>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                </Tooltip>
                                            }
                                        >
                                            <div style={{ cursor: 'help' }}>
                                                <div className={`fw-bold h5 text-${getGainLossColor(trade.total_gain_loss)}`}>
                                                    {formatCurrency(trade.total_gain_loss)}
                                                    <span className="ms-2 small text-muted">({formatPercentage(trade.total_gain_loss_pct)})</span>
                                                </div>
                                                <div className="text-muted" style={{ borderBottom: '1px dotted currentColor', display: 'inline-block' }}>
                                                    Total Gain
                                                </div>
                                            </div>
                                        </OverlayTrigger>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>



                {/* Transaction History Section with Status Column */}
                <Row className="mb-4">
                    <Col>
                        <div className="trade-details-table-container">
                            <div className="table-responsive">
                                {trade.transactions && trade.transactions.length > 0 ? (
                                    <Table className="mb-0 trade-details-table">
                                        <thead>
                                            <tr>
                                                <th className="py-2 px-3" style={{ width: '150px', cursor: 'pointer' }} onClick={() => handleTxSort('date')}>Date{txSortIndicator('date')}</th>
                                                <th className="py-2 px-3" style={{ width: '150px', cursor: 'pointer' }} onClick={() => handleTxSort('type')}>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span>Type{txSortIndicator('type')}</span>
                                                        <ButtonGroup size="sm" onClick={e => e.stopPropagation()}>
                                                            {['BOTH', 'BUY', 'SELL'].map(f => (
                                                                <Button
                                                                    key={f}
                                                                    variant={txTypeFilter === f ? (f === 'BUY' ? 'success' : f === 'SELL' ? 'danger' : 'secondary') : 'outline-secondary'}
                                                                    onClick={() => setTxTypeFilter(f)}
                                                                    style={{ padding: '1px 5px', fontSize: '0.7rem', lineHeight: '1.2' }}
                                                                >
                                                                    {f === 'BOTH' ? 'All' : f === 'BUY' ? 'Buy' : 'Sell'}
                                                                </Button>
                                                            ))}
                                                        </ButtonGroup>
                                                    </div>
                                                </th>
                                                <th className="py-2 px-3" style={{ width: '85px', cursor: 'pointer' }} onClick={() => handleTxSort('account')}>Account{txSortIndicator('account')}</th>
                                                <th className="py-2 px-3 text-end" style={{ width: '85px', cursor: 'pointer' }} onClick={() => handleTxSort('shares')}>Shares{txSortIndicator('shares')}</th>
                                                <th className="py-2 px-3 text-end" style={{ width: '113px', cursor: 'pointer' }} onClick={() => handleTxSort('price')}>Price{txSortIndicator('price')}</th>
                                                <th className="py-2 px-3 text-end" style={{ width: '130px', cursor: 'pointer' }} onClick={() => handleTxSort('total')}>Total{txSortIndicator('total')}</th>
                                                <th className="py-2 px-3 text-end" style={{ width: '130px', cursor: 'pointer' }} onClick={() => handleTxSort('profit$')}>Profit${txSortIndicator('profit$')}</th>
                                                <th className="py-2 px-3 text-end" style={{ width: '90px', cursor: 'pointer' }} onClick={() => handleTxSort('profit%')}>Profit%{txSortIndicator('profit%')}</th>
                                                <th className="py-2 px-3 text-center" style={{ width: '220px', cursor: 'pointer' }} onClick={() => handleTxSort('status')}>Status{txSortIndicator('status')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                // Pre-compute all derived values keyed by transaction_id so we can sort independently of display
                                                const txComputedData = {};
                                                trade.transactions.forEach(transaction => {
                                                    let statusText = '';
                                                    let profitValue = 0;
                                                    let profitPctValue = 0;
                                                    let profitAmount = '';
                                                    let profitPercentage = '';
                                                    let realizedProfit = 0;
                                                    let unrealizedProfit = 0;
                                                    let remaining = 0;
                                                    let soldShares = 0;

                                                    if (transaction.transaction_type === 'BUY') {
                                                        // Both LIFO-matched: remaining shares come from the backend's surviving
                                                        // lot walk, realized profit from whichever sells actually consumed this lot.
                                                        remaining = transaction.remaining_shares ?? 0;
                                                        if (isEffectivelyZero(remaining)) {
                                                            statusText = 'Sold';
                                                        } else if (Math.abs(remaining - transaction.shares) < SHARES_TOLERANCE) {
                                                            statusText = 'Holding';
                                                        } else {
                                                            statusText = `Holding (${Math.round(remaining)}/${Math.round(transaction.shares)})`;
                                                        }
                                                        const buyPrice = Number(transaction.price_per_share) || 0;
                                                        const txShares = Number(transaction.shares) || 0;
                                                        realizedProfit = transaction.realized_gain_amount ?? 0;
                                                        const currentPrice = trade.current_price ? Number(trade.current_price) : 0;
                                                        unrealizedProfit = remaining > 0 && currentPrice ? (currentPrice - buyPrice) * remaining : 0;
                                                        const totalProfit = realizedProfit + unrealizedProfit;
                                                        soldShares = txShares - remaining;
                                                        if (txShares > 0) {
                                                            const txCost = txShares * buyPrice;
                                                            profitValue = totalProfit;
                                                            profitAmount = formatCurrency(totalProfit);
                                                            if (txCost > 0) {
                                                                profitPctValue = (totalProfit / txCost) * 100;
                                                                profitPercentage = `${profitPctValue.toFixed(2)}%`;
                                                            }
                                                        }
                                                    } else if (transaction.transaction_type === 'SELL') {
                                                        // LIFO-matched realized gain, computed server-side against the actual buy lots.
                                                        profitValue = transaction.realized_gain_amount ?? 0;
                                                        profitPctValue = transaction.realized_gain_percent ?? 0;
                                                        profitAmount = formatCurrency(profitValue);
                                                        profitPercentage = `${profitPctValue.toFixed(2)}%`;
                                                    }
                                                    txComputedData[transaction.transaction_id] = {
                                                        statusText, profitValue, profitPctValue,
                                                        profitAmount, profitPercentage,
                                                        realizedProfit, unrealizedProfit, remaining, soldShares
                                                    };
                                                });

                                                // Sort transactions for display (txComputedData is keyed by id and unaffected by display order)
                                                const sortedTransactions = [...trade.transactions].sort((a, b) => {
                                                    const dir = txSort.dir === 'asc' ? 1 : -1;
                                                    const aD = txComputedData[a.transaction_id] || {};
                                                    const bD = txComputedData[b.transaction_id] || {};
                                                    switch (txSort.col) {
                                                        case 'type': return dir * a.transaction_type.localeCompare(b.transaction_type);
                                                        case 'account': return dir * (a.account_name || '').localeCompare(b.account_name || '');
                                                        case 'shares': return dir * (Number(a.shares) - Number(b.shares));
                                                        case 'price': return dir * (Number(a.price_per_share) - Number(b.price_per_share));
                                                        case 'total': return dir * (Number(a.total_amount) - Number(b.total_amount));
                                                        case 'profit$': return dir * ((aD.profitValue || 0) - (bD.profitValue || 0));
                                                        case 'profit%': return dir * ((aD.profitPctValue || 0) - (bD.profitPctValue || 0));
                                                        case 'status': return dir * (aD.statusText || '').localeCompare(bD.statusText || '');
                                                        default: { // 'date' and 'entry' (entry is trade-level, identical for all rows)
                                                            const dateDiff = new Date(a.transaction_date) - new Date(b.transaction_date);
                                                            return dir * (dateDiff !== 0 ? dateDiff : (a.transaction_id || 0) - (b.transaction_id || 0));
                                                        }
                                                    }
                                                });

                                                // Apply type filter after sort (txComputedData above is always built from all transactions)
                                                const visibleTransactions = txTypeFilter === 'BOTH'
                                                    ? sortedTransactions
                                                    : sortedTransactions.filter(tx => tx.transaction_type === txTypeFilter);

                                                // Render transaction rows using pre-computed data
                                                const transactionRows = visibleTransactions.map((transaction) => {
                                                    const { statusText: status, profitAmount, profitPercentage,
                                                            realizedProfit, unrealizedProfit, remaining, soldShares } = txComputedData[transaction.transaction_id] || {};

                                                    return (
                                                        <tr key={transaction.transaction_id}
                                                            className={
                                                                transaction.transaction_type === 'BUY' && (status || '').startsWith('Sold') ? 'sold-row' : ''
                                                            }
                                                        >
                                                            <td className="py-2 px-3" style={{ width: '150px' }}>
                                                                {formatDateWithWeekday(transaction.transaction_date)}
                                                            </td>
                                                            <td className="py-2 px-3" style={{ width: '150px' }}>
                                                                <Badge bg={transaction.transaction_type === 'BUY' ? 'success' : 'danger'}>
                                                                    {transaction.transaction_type}
                                                                </Badge>
                                                            </td>
                                                            <td className="py-2 px-3" style={{ width: '85px' }}>
                                                                {transaction.account_id ? (
                                                                    <Link
                                                                        to={`/account/${transaction.account_id}`}
                                                                        className="text-decoration-none text-primary"
                                                                        title="Go to Account Details"
                                                                    >
                                                                        {transaction.account_name}
                                                                    </Link>
                                                                ) : (
                                                                    transaction.account_name
                                                                )}
                                                            </td>
                                                            <td className="py-2 px-3 text-end" style={{ width: '85px' }}>
                                                                {transaction.shares?.toLocaleString()}
                                                            </td>
                                                            <td className="py-2 px-3 text-end" style={{ width: '113px' }}>
                                                                {formatCurrency(transaction.price_per_share)}
                                                            </td>
                                                            <td className="py-2 px-3 text-end" style={{ width: '130px' }}>
                                                                {formatCurrency(transaction.total_amount)}
                                                            </td>
                                                            <td className={`py-2 px-3 text-end ${profitAmount ? (profitAmount.startsWith('$') && parseFloat(profitAmount.replace('$', '').replace(',', '')) >= 0 ? 'text-success' : 'text-danger') : ''}`} style={{ width: '130px' }}>
                                                                {transaction.transaction_type === 'BUY' && (status || '').includes('Holding (') ? (
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={
                                                                            <Tooltip id={`profit-tooltip-${transaction.transaction_id}`} style={{
                                                                                '--bs-tooltip-bg': 'white',
                                                                                '--bs-tooltip-color': '#212529',
                                                                                '--bs-tooltip-max-width': '1200px'
                                                                            }}>
                                                                                <div style={{ textAlign: 'left', color: '#212529' }}>
                                                                                    <strong>Profit Breakdown:</strong>
                                                                                    {(() => {
                                                                                        const buyPrice = Number(transaction.price_per_share) || 0;
                                                                                        const currentPrice = trade.current_price ? Number(trade.current_price) : 0;
                                                                                        const totalProfit = (realizedProfit || 0) + (unrealizedProfit || 0);

                                                                                        return (
                                                                                            <>
                                                                                                <div className="mt-1 text-muted small">
                                                                                                    Buy price: {formatCurrency(buyPrice)}/share
                                                                                                </div>
                                                                                                <div className="mt-1">
                                                                                                    Realized (from {Math.round(soldShares || 0)} sold):
                                                                                                </div>
                                                                                                <div className="text-end">
                                                                                                    <span className={(realizedProfit || 0) >= 0 ? 'text-success' : 'text-danger'}>{formatCurrency(realizedProfit || 0)}</span>
                                                                                                </div>
                                                                                                <div className="mt-1">
                                                                                                    Unrealized (from {Math.round(remaining || 0)} held at {formatCurrency(currentPrice)}):
                                                                                                </div>
                                                                                                <div className="text-end">
                                                                                                    <span className={(unrealizedProfit || 0) >= 0 ? 'text-success' : 'text-danger'}>{formatCurrency(unrealizedProfit || 0)}</span>
                                                                                                </div>
                                                                                                <div className="mt-1 pt-1" style={{ borderTop: '1px solid #dee2e6', whiteSpace: 'nowrap' }}>
                                                                                                    <strong>{formatCurrency(realizedProfit || 0)} + {formatCurrency(unrealizedProfit || 0)} = <span className={totalProfit >= 0 ? 'text-success' : 'text-danger'}>{formatCurrency(totalProfit)}</span></strong>
                                                                                                </div>
                                                                                            </>
                                                                                        );
                                                                                    })()}
                                                                                </div>
                                                                            </Tooltip>
                                                                        }
                                                                    >
                                                                        <span style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}>
                                                                            {profitAmount}
                                                                        </span>
                                                                    </OverlayTrigger>
                                                                ) : (
                                                                    profitAmount
                                                                )}
                                                            </td>
                                                            <td className={`py-2 px-3 text-end ${profitPercentage ? (parseFloat(profitPercentage.replace('%', '')) >= 0 ? 'text-success' : 'text-danger') : ''}`} style={{ width: '90px' }}>
                                                                {profitPercentage}
                                                            </td>
                                                            <td className="py-2 px-3 text-center" style={{ width: '220px' }}>
                                                                {status}
                                                            </td>
                                                        </tr>
                                                    );
                                                });

                                                return transactionRows;
                                            })()}
                                        </tbody>
                                        {trade.status === 'CLOSED' && (() => {
                                            // Calculate total trade profit for closed trades
                                            const sellTransactions = trade.transactions.filter(tx => tx.transaction_type === 'SELL');
                                            const buyTransactions = trade.transactions.filter(tx => tx.transaction_type === 'BUY');
                                            
                                            let totalProfit = 0;
                                            let totalCostBasis = 0;
                                            
                                            if (sellTransactions.length > 0) {
                                                // Calculate total proceeds from all sells
                                                const totalProceeds = sellTransactions.reduce((total, tx) => {
                                                    return total + (Number(tx.shares) * Number(tx.price_per_share));
                                                }, 0);
                                                
                                                // Calculate total cost basis using LIFO
                                                const totalSharesSold = sellTransactions.reduce((total, tx) => total + Number(tx.shares), 0);
                                                
                                                // Sort buy transactions by date (most recent first for LIFO)
                                                const sortedBuys = buyTransactions.sort((a, b) => 
                                                    new Date(b.transaction_date) - new Date(a.transaction_date)
                                                );
                                                
                                                let sharesToAllocate = totalSharesSold;
                                                
                                                for (const buyTx of sortedBuys) {
                                                    if (sharesToAllocate <= 0) break;
                                                    
                                                    const buyShares = Math.min(sharesToAllocate, Number(buyTx.shares));
                                                    const buyPrice = Number(buyTx.price_per_share);
                                                    
                                                    totalCostBasis += buyShares * buyPrice;
                                                    sharesToAllocate -= buyShares;
                                                }
                                                
                                                totalProfit = totalProceeds - totalCostBasis;
                                            }
                                            
                                            const totalProfitPercentage = totalCostBasis > 0 ? (totalProfit / totalCostBasis) * 100 : 0;
                                            
                                            return (
                                                <tfoot>
                                                    <tr className="table-total">
                                                        <td className="py-2 px-3 fw-bold" style={{ width: '150px' }}>Total Trade</td>
                                                        <td className="py-2 px-3" style={{ width: '150px' }}></td>
                                                        <td className="py-2 px-3" style={{ width: '85px' }}></td>
                                                        <td className="py-2 px-3" style={{ width: '85px' }}></td>
                                                        <td className="py-2 px-3" style={{ width: '113px' }}></td>
                                                        <td className="py-2 px-3" style={{ width: '130px' }}></td>
                                                        <td className="py-2 px-3 text-end fw-bold" style={{ width: '130px' }}>
                                                            <span className={`fw-bold ${totalProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                                                                {formatCurrency(totalProfit)}
                                                            </span>
                                                        </td>
                                                        <td className="py-2 px-3 text-end fw-bold" style={{ width: '90px' }}>
                                                            <span className={`fw-bold ${totalProfitPercentage >= 0 ? 'text-success' : 'text-danger'}`}>
                                                                {totalProfitPercentage.toFixed(2)}%
                                                            </span>
                                                        </td>
                                                        <td className="py-2 px-3 text-center fw-bold" style={{ width: '220px' }}>
                                                            <Badge bg={totalProfit >= 0 ? 'success' : 'danger'} className="px-2 py-1">
                                                                {trade.status}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            );
                                        })()}
                                    </Table>
                                ) : (
                                    <Placeholder as={Card.Body} animation="glow">
                                        <Placeholder xs={12} size="lg" />
                                        <Placeholder xs={12} size="lg" />
                                        <Placeholder xs={12} size="lg" />
                                    </Placeholder>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Stop Losses & Purchase Positions section (Post Analysis injected within when CLOSED) */}
                <Row className="mb-4">
                    <Col>
                        {renderPurchasePositions()}
                    </Col>
                </Row>

            </Container>
        </>
    );
};

export default TradeDetails;
